import { useDisclosure } from '@chakra-ui/react';
import { IconFileText } from 'central-icons/IconFileText';
import { IconWindowCursor } from 'central-icons/IconWindowCursor';
import { useEffect, useMemo, useState } from 'react';

import { MenuItem } from '@revelio/ui';

import { useBrand } from '../../../../shared/branding/brand-provider';
import type { PdfCoverParams } from '../Chart/chart-menu/data-download/pdf-export/pdf-exporter';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { getFileName } from '../Chart/utils/getFileName';
import { PdfPreviewModal } from '../Modal/PdfPreviewModal';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType, RevelioPageId } from '../PageConfigProvider/types';
import { useCoverSelectedParameters } from '../Reports/useCoverSelectedParameters';

export const PdfExportMenuItem = () => {
  const {
    exportAllChartsToPdf,
    previewAllChartsPdf,
    isExporting,
    pdfExportRef,
    setDefaultExportFileName,
  } = usePageExport();
  const { config: pageConfig } = usePageConfig();
  const { config: tabContent } = useTabContentConfig();
  const brand = useBrand();
  const coverParameters = useCoverSelectedParameters();

  const buildCoverParams = (): PdfCoverParams => {
    // Prefer the PNG variant (`coverImagePortraitPpt`) when set — jsPDF's
    // canvas-rasterization path is more reliable on PNGs — and fall back to
    // the portrait SVG (`coverImagePortrait`) so brands that follow the
    // documented SVG-only pattern still get their cover on the PDF.
    const coverImageUrl =
      brand.coverImagePortraitPpt ?? brand.coverImagePortrait;
    const hasBrandCover = !!coverImageUrl;
    return {
      title: tabContent.title ?? '',
      description: tabContent.description ?? '',
      parameters: coverParameters,
      brand: {
        displayName: brand.displayName,
        coverImageUrl,
        coverBackgroundColor: brand.coverBackgroundColor,
        coverTitleColor: brand.coverTitleColor,
        coverDescriptionColor: brand.coverDescriptionColor,
        // Default-brand fallback: vertical gradient from sidebarColor[900]
        // (top, dark) to sidebarColor[500] (bottom, lighter) — mirrors the
        // CSS `linear-gradient(to bottom, ...)` in `ReportCoverPage.tsx`.
        // Only applied when the brand has no cover image of its own.
        fallbackGradientFrom: hasBrandCover
          ? undefined
          : brand.sidebarColor[900],
        fallbackGradientTo: hasBrandCover ? undefined : brand.sidebarColor[500],
        reportLogoUrl: brand.logos.reportLogo,
      },
    };
  };

  const isMenuItemVisible =
    pageConfig.revelioPageId !== RevelioPageId.TALENT_DISCOVERY &&
    pageConfig.type === PageType.REPORT;

  const defaultFileName = useMemo(
    () =>
      getFileName({
        primaryFilterId: tabContent.primaryFilter?.id,
        pageName: pageConfig.name,
      }),
    [tabContent, pageConfig]
  );

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const {
    isOpen: isPreviewOpen,
    onOpen: openPreview,
    onClose: closePreview,
  } = useDisclosure();

  const handleClosePreview = () => {
    closePreview();

    setTimeout(() => {
      if (previewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
    }, 300);
  };

  useEffect(() => {
    setDefaultExportFileName(defaultFileName);
  }, [defaultFileName, setDefaultExportFileName]);

  // The cover page is generated directly via jsPDF (see `drawCoverPage` in
  // pdf-exporter.ts), so we no longer need an offscreen render of the cover.
  // The chart pages are captured from the live ContentGrid which is already
  // mounted and has its data loaded — no re-rendering or re-fetching at
  // export time.
  const handleExportPdf = async () => {
    if (!pdfExportRef.current) {
      console.warn('PDF export root not registered.');
      return;
    }
    try {
      await exportAllChartsToPdf({ cover: buildCoverParams() });
    } catch (error) {
      console.error('Error exporting PDF:', error);
    }
  };

  const handlePreviewPdf = async () => {
    if (!pdfExportRef.current) {
      console.warn('PDF export root not registered.');
      return;
    }
    try {
      const url = await previewAllChartsPdf({ cover: buildCoverParams() });
      if (url) {
        setPreviewUrl(url);
        openPreview();
      }
    } catch (e) {
      console.error('Error previewing dashboard PDF:', e);
    }
  };

  if (!isMenuItemVisible) {
    return null;
  }

  return (
    <>
      <MenuItem
        onClick={handleExportPdf}
        disabled={isExporting}
        icon={<IconFileText size={16} />}
        title="Download PDF"
      >
        Download PDF
      </MenuItem>
      <MenuItem
        onClick={handlePreviewPdf}
        disabled={isExporting}
        icon={<IconWindowCursor size={16} />}
        title="Preview PDF"
      >
        Preview PDF
      </MenuItem>
      <PdfPreviewModal
        isOpen={isPreviewOpen}
        onClose={handleClosePreview}
        title={defaultFileName || 'PDF file preview'}
        previewUrl={previewUrl ?? undefined}
      />
    </>
  );
};
